import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ArrowLeft, MoreHorizontal, FolderOpen, Receipt, Pencil, Trash2, Info, ShieldCheck, ImagePlus, ImageOff } from "lucide-react";
import { toast } from "sonner";
import AddPropertyDialog from "@/components/AddPropertyDialog";
import Logo from "@/components/Logo";
import DocumentsTab from "@/components/DocumentsTab";
import RentTab from "@/components/RentTab";
import GstTab from "@/components/GstTab";
import InfoTab from "@/components/InfoTab";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user && user !== false && user.role === "admin";
  const [property, setProperty] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tab, setTab] = useState("documents");
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/properties/${id}`);
      setProperty(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      navigate("/");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleEdit = async (payload) => {
    try {
      await api.put(`/properties/${id}`, payload);
      toast.success("Property updated");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Property deleted");
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB).");
      return;
    }
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/properties/${id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover photo updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingCover(false);
    }
  };

  const handleRemoveCover = async () => {
    try {
      await api.delete(`/properties/${id}/cover`);
      toast.success("Cover photo removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="h-8 w-8 rounded-full border-2 border-emerald-900 border-t-transparent animate-spin" />
      </div>
    );
  }

  const tabTrigger = "border-b-2 border-transparent data-[state=active]:border-emerald-900 data-[state=active]:text-emerald-900 text-stone-500 bg-transparent rounded-none h-14 px-4 sm:px-6 text-base sm:text-lg font-medium gap-2 shadow-none hover:text-stone-800";

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center text-sm text-stone-600 hover:text-emerald-900 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            All properties
          </button>
          <Logo size={48} showWordmark vertical wordmarkClassName="text-stone-900 text-base sm:text-lg" className="hidden sm:flex" />
          {isAdmin ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-stone-600" data-testid="property-menu">
                  <MoreHorizontal className="h-5 w-5" strokeWidth={1.5} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => coverInputRef.current?.click()} disabled={uploadingCover} data-testid="upload-cover">
                  <ImagePlus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  {uploadingCover ? "Uploading…" : (property.has_cover ? "Replace cover photo" : "Upload cover photo")}
                </DropdownMenuItem>
                {property.has_cover && (
                  <DropdownMenuItem onClick={handleRemoveCover} data-testid="remove-cover">
                    <ImageOff className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Remove cover photo
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setEditOpen(true)} data-testid="edit-property">
                  <Pencil className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Edit details
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setConfirmDelete(true)} className="text-red-700 focus:text-red-700" data-testid="delete-property">
                  <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Delete property
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span className="w-9" />
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-2">Property</p>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-stone-900" data-testid="property-name">
            {property.name}
          </h1>
          <p className="mt-2 text-stone-600">
            Tenant: <span className="text-stone-800">{property.tenant_name || "—"}</span>
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-transparent border-b border-stone-200 rounded-none w-full justify-start h-auto p-0 overflow-x-auto">
            <TabsTrigger value="documents" className={tabTrigger} data-testid="tab-documents">
              <FolderOpen className="h-4 w-4" strokeWidth={1.5} />
              Documents
            </TabsTrigger>
            <TabsTrigger value="rent" className={tabTrigger} data-testid="tab-rent">
              <Receipt className="h-4 w-4" strokeWidth={1.5} />
              Rent
            </TabsTrigger>
            <TabsTrigger value="gst" className={tabTrigger} data-testid="tab-gst">
              <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
              GST Status
            </TabsTrigger>
            <TabsTrigger value="info" className={tabTrigger} data-testid="tab-info">
              <Info className="h-4 w-4" strokeWidth={1.5} />
              Information
            </TabsTrigger>
          </TabsList>

          <TabsContent value="documents" className="pt-8">
            <DocumentsTab propertyId={id} />
          </TabsContent>
          <TabsContent value="rent" className="pt-8">
            <RentTab propertyId={id} />
          </TabsContent>
          <TabsContent value="gst" className="pt-8">
            <GstTab propertyId={id} />
          </TabsContent>
          <TabsContent value="info" className="pt-8">
            <InfoTab propertyId={id} initialTenantName={property.tenant_name} />
          </TabsContent>
        </Tabs>
      </main>

      <AddPropertyDialog open={editOpen} onOpenChange={setEditOpen} onSubmit={handleEdit} initial={property} />

      <input
        ref={coverInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleCoverChange}
        data-testid="cover-file-input"
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this property?</AlertDialogTitle>
            <AlertDialogDescription>
              All rent records, contact info and documents linked to this property will be removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-700 hover:bg-red-800 text-white" data-testid="delete-confirm-button">
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
